import { HttpClient, HttpContext, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { lastValueFrom, Observable } from "rxjs";

const SESSION_ACCESS_TOKEN_KEY = "kc_access_token";

function getCurrentSessionToken(fallbackToken?: string): string | undefined {
    try {
        const sessionToken = typeof sessionStorage !== "undefined"
            ? sessionStorage.getItem(SESSION_ACCESS_TOKEN_KEY)
            : null;
        return sessionToken || fallbackToken || undefined;
    } catch {
        return fallbackToken || undefined;
    }
}

/**
 * Generic requests handler for the API
 * @param {HttpClient} http The HttpClient service
 * @param {string} method The HTTP method
 * @param {string} url The URL to send the request to
 * @param {string} [token] The optional token to be added to the headers
 * @param {any} [body] The optional body of the request
 * @param {Record<string, string>} [extraHeaders] Optional extra headers to add to the request
 * @param {HttpContext} [context] Optional HttpContext (e.g. to opt out of the auth interceptor's redirect)
 * @returns {Promise<{ data?: T; error?: any }>} The response data or error
 */
export async function handleRequest<T>(
    http: HttpClient,
    method: string,
    url: string,
    token?: string,
    body?: any,
    extraHeaders?: Record<string, string>,
    context?: HttpContext
): Promise<{ data?: T; error?: any }> {
    try {
        let headers = new HttpHeaders();
        const resolvedToken = getCurrentSessionToken(token);
        if (resolvedToken) {
            headers = headers.set("Authorization", `Bearer ${resolvedToken}`);
        }
        if (extraHeaders) {
            for (const [key, value] of Object.entries(extraHeaders)) {
                headers = headers.set(key, value);
            }
        }

        let observable: Observable<T>;

        switch (method) {
            case "GET":
                observable = http.get<T>(url, { headers, context });
                break;
            case "POST":
                observable = http.post<T>(url, body, { headers, context });
                break;
            case "PATCH":
                observable = http.patch<T>(url, body, { headers, context });
                break;
            case "PUT":
                observable = http.put<T>(url, body, { headers, context });
                break;
            case "DELETE":
                observable = http.delete<T>(url, { headers, body, context });
                break;
            default:
                throw new Error("Invalid request method");
        }

        const data = await lastValueFrom(observable);
        
        return { data };
    } catch (error) {
        const httpError = error instanceof HttpErrorResponse ? error : new Error(String(error));
        console.error('[handleRequest]', method, url, httpError);
        return { error: httpError };
    }
}
